import { createTheme } from '@mui/material/styles';

// Site2 — alternate UI preview, modeled on the NestHub aesthetic.
// Light page background with white cards, near-black sidebar/headings,
// pastel status pills, comfortable density, subtle shadows.
//
// Colors are kept conservative on purpose: this is a long-shift dashboard,
// not a marketing site. Accent color is reserved for the active sidebar
// item and primary CTAs only.

// Palette — exposed so Layout/Sidebar components can reference the same
// tokens without redefining them.
export const palette = {
  // Sidebar — near-black with warm undertone, NOT pure black
  sidebarBg: '#0f0f10',
  sidebarBgHover: '#27272a',
  sidebarText: '#fafafa',          // bumped from #e8e8e8 — was too dim
  sidebarTextMuted: '#c4c4c7',     // bumped from #9a9a9d — child links need to be readable
  sidebarBorder: '#2a2a2c',
  sidebarActive: '#ffffff',
  sidebarActiveBg: '#27272a',

  // Page content
  pageBg: '#f5f6f7',
  cardBg: '#ffffff',
  cardBorder: '#e5e7eb',

  // Text on light bg
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',

  // Accent — used sparingly. Coral/red like NestHub's CTA pill.
  accent: '#ef4444',
  accentHover: '#dc2626',

  // Pastel status pills (matches NestHub palette)
  pillPaidBg: '#d1fae5',
  pillPaidText: '#065f46',
  pillPendingBg: '#fed7aa',
  pillPendingText: '#9a3412',
  pillOpenBg: '#dbeafe',
  pillOpenText: '#1e40af',
  pillResolvedBg: '#d1fae5',
  pillResolvedText: '#065f46',
  pillInProgressBg: '#fef3c7',
  pillInProgressText: '#92400e',

  // Soft icon-tint backgrounds (the rounded square icon containers)
  iconTintBlue: '#dbeafe',
  iconTintGreen: '#d1fae5',
  iconTintOrange: '#ffedd5',
  iconTintPurple: '#ede9fe',
};

const themeSite2 = createTheme({
  palette: {
    primary: {
      main: palette.textPrimary,
      contrastText: '#ffffff',
    },
    secondary: {
      main: palette.accent,
    },
    background: {
      default: palette.pageBg,
      paper: palette.cardBg,
    },
    text: {
      primary: palette.textPrimary,
      secondary: palette.textSecondary,
    },
    success: {
      main: '#10b981',
      light: palette.pillPaidBg,
    },
    warning: {
      main: '#f59e0b',
      light: palette.pillInProgressBg,
    },
    error: {
      main: palette.accent,
    },
    info: {
      main: '#3b82f6',
      light: palette.pillOpenBg,
    },
    divider: palette.cardBorder,
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      color: palette.textPrimary,
      letterSpacing: '-0.02em',
      '@media (max-width:600px)': { fontSize: '1.5rem' },
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: palette.textPrimary,
      letterSpacing: '-0.015em',
      '@media (max-width:600px)': { fontSize: '1.25rem' },
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: palette.textPrimary,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: palette.textPrimary,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      color: palette.textPrimary,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: palette.textPrimary,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
    body1: {
      fontSize: '0.9375rem',
      color: palette.textPrimary,
      lineHeight: 1.55,
    },
    body2: {
      fontSize: '0.875rem',
      color: palette.textSecondary,
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: 0,
    },
    caption: {
      fontSize: '0.75rem',
      color: palette.textMuted,
    },
  },
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.pageBg,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
        },
        containedPrimary: {
          backgroundColor: palette.textPrimary,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#1e293b',
            boxShadow: 'none',
          },
        },
        containedSecondary: {
          backgroundColor: palette.accent,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: palette.accentHover,
            boxShadow: 'none',
          },
        },
        outlinedPrimary: {
          borderColor: palette.cardBorder,
          color: palette.textPrimary,
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f9fafb',
            borderColor: palette.textSecondary,
          },
        },
        textPrimary: {
          color: palette.textPrimary,
          '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.04)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#ffffff',
            fontSize: '0.9375rem',
            '& fieldset': {
              borderColor: palette.cardBorder,
            },
            '&:hover fieldset': {
              borderColor: palette.textSecondary,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.textPrimary,
              borderWidth: '1.5px',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.textSecondary,
          fontSize: '0.9375rem',
          '&.Mui-focused': {
            color: palette.textPrimary,
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: 10,
          backgroundColor: palette.cardBg,
          boxShadow: 'none',
        },
        elevation0: {
          border: `1px solid ${palette.cardBorder}`,
        },
        elevation1: {
          border: `1px solid ${palette.cardBorder}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${palette.cardBorder}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          backgroundColor: palette.cardBg,
        },
      },
    },
    MuiAppBar: {
      // Site2 uses a sidebar — the AppBar is hidden by Site2Layout, but
      // keep these defaults sensible in case any page renders one inline.
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: palette.textPrimary,
          boxShadow: 'none',
          borderBottom: `1px solid ${palette.cardBorder}`,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '100% !important',
          padding: '0 24px',
          boxSizing: 'border-box',
          '@media (max-width:600px)': { padding: '0 16px' },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          flexWrap: 'wrap',
          width: '100%',
          margin: '0 !important',
          padding: '0 !important',
          boxSizing: 'border-box',
          '@media (max-width:600px)': { flexDirection: 'column' },
        },
        item: {
          minWidth: 0,
          wordWrap: 'break-word',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.textPrimary,
          color: '#ffffff',
          fontSize: '0.8125rem',
          padding: '6px 10px',
          borderRadius: 6,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
        },
        // Pastel variants — set chip color="success"|"warning" etc and
        // these tints kick in.
        colorSuccess: {
          backgroundColor: palette.pillPaidBg,
          color: palette.pillPaidText,
        },
        colorWarning: {
          backgroundColor: palette.pillInProgressBg,
          color: palette.pillInProgressText,
        },
        colorError: {
          backgroundColor: '#fee2e2',
          color: '#991b1b',
        },
        colorInfo: {
          backgroundColor: palette.pillOpenBg,
          color: palette.pillOpenText,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.cardBorder}`,
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 600,
          color: palette.textSecondary,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: '#fafafa',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.cardBorder,
        },
      },
    },
  },
});

export default themeSite2;
